import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { contactDetailApi } from '../lib/api'
import type { UpdateContactRequest, CreateContactRequest } from '../types/contactDetail'

export function useContactDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['contacts', id, 'detail'],
    queryFn: async () => {
      if (!id) throw new Error('Contact ID is required')
      const response = await contactDetailApi.getDetail(id)
      return response.data
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  })
}

export function useCreateContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateContactRequest) => {
      const response = await contactDetailApi.createContact(data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useUpdateContact(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateContactRequest) => {
      const response = await contactDetailApi.updateContact(id, data)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['contacts', id, 'detail'], data)
      queryClient.invalidateQueries({ queryKey: ['contacts', 'search'] })
    },
  })
}

export function useAssignTag(contactId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tagId: string) =>
      contactDetailApi.assignTag(contactId, tagId).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', contactId, 'detail'] })
      queryClient.invalidateQueries({ queryKey: ['tags'] })
    },
  })
}

export function useRemoveTag(contactId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tagId: string) =>
      contactDetailApi.removeTag(contactId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', contactId, 'detail'] })
      queryClient.invalidateQueries({ queryKey: ['tags'] })
    },
  })
}
